<script>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useTrack } from 'dashboard/composables';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { PILOT_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { usePilotConfigStore } from 'dashboard/store/pilot/preferences';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import TasksAPI from 'dashboard/api/pilot/tasks';

const CACHE_TTL = 5 * 60 * 1000;

export default {
  name: 'LabelSuggestion',
  components: { Icon },
  props: {
    chatLabels: {
      type: Array,
      default: () => [],
    },
    contactId: {
      type: [Number, String],
      default: null,
    },
    disableAutoFetch: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isCloudFeatureEnabled } = useAccount();
    const pilotConfigStore = usePilotConfigStore();

    const pilotTasksEnabled = computed(() =>
      isCloudFeatureEnabled(FEATURE_FLAGS.PILOT_TASKS)
    );

    const isLabelSuggestionEnabled = computed(() => {
      if (!pilotTasksEnabled.value) return false;
      return pilotConfigStore.features?.label_suggestion?.enabled === true;
    });

    return {
      pilotConfigStore,
      pilotTasksEnabled,
      isLabelSuggestionEnabled,
    };
  },
  data() {
    return {
      isLoading: false,
      suggestedLabels: [],
      selectedLabels: [],
      selectedNewLabels: [],
      isDismissed: false,
      isHovered: false,
      hasAnalyzed: false,
      autoFetchedConversationId: null,
      lastError: null,
    };
  },
  computed: {
    ...mapGetters({
      allLabels: 'labels/getLabels',
      currentAccountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
    }),
    conversationId() {
      return this.currentChat?.id;
    },
    messageCount() {
      return this.currentChat?.messages?.length || 0;
    },
    hasEnoughContent() {
      return this.messageCount >= 2;
    },
    // 功能是否启用
    featureEnabled() {
      return this.pilotTasksEnabled && this.isLabelSuggestionEnabled;
    },
    // 是否应该显示组件
    shouldShow() {
      if (!this.featureEnabled) return false;
      if (this.isDismissed) return false;
      // 加载中 / 有结果 / 已分析但无结果 → 显示
      return (
        this.isLoading || this.suggestedLabels.length > 0 || this.hasAnalyzed
      );
    },
    // 是否显示"无结果"提示
    showNoResult() {
      return this.hasAnalyzed && this.suggestedLabels.length === 0;
    },
    // 已存在的标签（系统中有的）
    matchedLabels() {
      if (!this.allLabels || !this.suggestedLabels) return [];
      return this.allLabels.filter(label =>
        this.suggestedLabels.some(
          s => s.toLowerCase() === label.title.toLowerCase()
        )
      );
    },
    // 不存在的标签（需要创建的）
    unmatchedLabels() {
      if (!this.allLabels || !this.suggestedLabels) return [];
      return this.suggestedLabels.filter(
        s =>
          !this.allLabels.some(l => l.title.toLowerCase() === s.toLowerCase())
      );
    },
    // 可以添加的标签（存在且未添加到会话）
    addableLabels() {
      return this.matchedLabels.filter(l => !this.isAlreadyAdded(l.title));
    },
    addButtonText() {
      const existCount = this.selectedLabels.length;
      const newCount = this.selectedNewLabels.length;
      const totalCount = existCount + newCount;

      if (totalCount === 0) {
        if (
          this.addableLabels.length === 0 &&
          this.unmatchedLabels.length === 0
        ) {
          return '';
        }
        return this.$t('LABEL_MGMT.SUGGESTIONS.ADD_ALL_LABELS');
      }
      if (totalCount === 1)
        return this.$t('LABEL_MGMT.SUGGESTIONS.ADD_SELECTED_LABEL');
      return this.$t('LABEL_MGMT.SUGGESTIONS.ADD_SELECTED_LABELS');
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler(newId, oldId) {
        if (!newId) return;

        // 切换会话时重置状态
        this.suggestedLabels = [];
        this.selectedLabels = [];
        this.selectedNewLabels = [];
        this.isLoading = false;
        this.hasAnalyzed = false;
        this.autoFetchedConversationId = null;

        // 检查是否已忽略
        this.isDismissed = this.checkDismissed();

        // 如果是同一个会话，不重复触发
        if (oldId && newId === oldId) return;

        // 自动触发（延迟等待消息加载）
        this.$nextTick(() => {
          setTimeout(() => this.autoFetch(), 500);
        });
      },
    },
    // 监听功能启用状态变化
    featureEnabled: {
      immediate: true,
      handler(enabled) {
        if (enabled && this.conversationId && !this.isDismissed) {
          this.$nextTick(() => {
            setTimeout(() => this.autoFetch(), 500);
          });
        }
      },
    },
    // 监听消息数量变化
    messageCount: {
      handler(newCount, oldCount) {
        // 消息从不足变为足够时，自动触发
        if (
          oldCount < 2 &&
          newCount >= 2 &&
          this.featureEnabled &&
          !this.isDismissed
        ) {
          this.autoFetch();
        }
      },
    },
    // 监听显示状态变化，通知父组件滚动
    shouldShow(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$emit('scroll-to-bottom');
          }, 100);
        });
      }
    },
  },
  methods: {
    // 自动触发（带缓存）
    async autoFetch() {
      // 功能未启用
      if (!this.featureEnabled) return;
      // 禁止自动触发（右侧面板实例）
      if (this.disableAutoFetch) return;
      // 已忽略
      if (this.isDismissed) return;
      // 同一会话只自动请求一次（防止多个 watcher 重复触发）
      if (this.autoFetchedConversationId === this.conversationId) return;
      this.autoFetchedConversationId = this.conversationId;

      // 检查缓存
      const cached = this.getCache();
      if (cached) {
        this.suggestedLabels = cached;
        return;
      }

      // 调用 API（让后端判断是否有足够内容）
      await this.fetchFromAPI();
    },

    // 手动触发（强制刷新）
    async trigger() {
      this.isDismissed = false;
      this.hasAnalyzed = false;
      this.suggestedLabels = [];
      this.lastError = null;
      this.clearCache();

      // 清除 localStorage 中的忽略状态，允许下次自动触发
      const storeName = `${LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS}::${this.currentAccountId}`;
      LocalStorage.deleteFromJsonStore(storeName, this.conversationId);

      await this.fetchFromAPI();
      return { labels: this.suggestedLabels, error: this.lastError };
    },

    // 从 API 获取
    async fetchFromAPI() {
      if (!this.conversationId) return;

      this.isLoading = true;

      try {
        // 确保配置已加载
        if (
          !this.pilotConfigStore.features ||
          Object.keys(this.pilotConfigStore.features).length === 0
        ) {
          await this.pilotConfigStore.fetch();
        }

        const result = await TasksAPI.labelSuggestion(this.conversationId);

        const {
          data: { message: labels },
        } = result;

        const cleanLabels = (labels || '')
          .toLowerCase()
          .split(',')
          .map(l => l.trim())
          .filter(l => l && l !== '""' && l !== "''")
          .filter((l, i, arr) => arr.indexOf(l) === i);

        this.suggestedLabels = cleanLabels;
        this.hasAnalyzed = true;

        if (cleanLabels.length > 0) {
          this.setCache(cleanLabels);
        }

        this.track(PILOT_EVENTS.LABEL_SUGGESTION_SHOWN);
      } catch (error) {
        if (error?.response?.status === 402) {
          this.lastError =
            error.response.data?.error ||
            this.$t('LABEL_MGMT.SUGGESTIONS.TOKEN_INSUFFICIENT');
        } else {
          this.lastError =
            error?.response?.data?.error ||
            this.$t('LABEL_MGMT.SUGGESTIONS.API_ERROR');
        }
      } finally {
        this.isLoading = false;
      }
    },

    isAlreadyAdded(title) {
      return this.chatLabels.some(l => l.toLowerCase() === title.toLowerCase());
    },

    isSelected(title) {
      return this.selectedLabels.some(
        l => l.toLowerCase() === title.toLowerCase()
      );
    },

    isNewSelected(title) {
      return this.selectedNewLabels.some(
        l => l.toLowerCase() === title.toLowerCase()
      );
    },

    toggleLabel(label) {
      if (this.isAlreadyAdded(label.title)) return;

      const idx = this.selectedLabels.findIndex(
        l => l.toLowerCase() === label.title.toLowerCase()
      );
      if (idx === -1) {
        this.selectedLabels.push(label.title);
      } else {
        this.selectedLabels.splice(idx, 1);
      }
    },

    toggleNewLabel(labelTitle) {
      const idx = this.selectedNewLabels.findIndex(
        l => l.toLowerCase() === labelTitle.toLowerCase()
      );
      if (idx === -1) {
        this.selectedNewLabels.push(labelTitle);
      } else {
        this.selectedNewLabels.splice(idx, 1);
      }
    },

    getCache() {
      const key = `label_suggestion_${this.currentAccountId}_${this.conversationId}`;
      const cached = LocalStorage.get(key);
      if (!cached) return null;

      const { data, timestamp } = cached;
      if (Date.now() - timestamp > CACHE_TTL) {
        LocalStorage.remove(key);
        return null;
      }
      return data;
    },

    setCache(data) {
      const key = `label_suggestion_${this.currentAccountId}_${this.conversationId}`;
      LocalStorage.set(key, { data, timestamp: Date.now() });
    },

    clearCache() {
      const key = `label_suggestion_${this.currentAccountId}_${this.conversationId}`;
      LocalStorage.remove(key);
    },

    async addLabels() {
      const existLabelsToAdd =
        this.selectedLabels.length > 0
          ? this.selectedLabels
          : this.addableLabels.map(l => l.title);

      const newLabelsToAdd =
        this.selectedNewLabels.length > 0
          ? this.selectedNewLabels
          : this.unmatchedLabels;

      if (existLabelsToAdd.length === 0 && newLabelsToAdd.length === 0) return;

      // 创建新标签
      for (const labelTitle of newLabelsToAdd) {
        try {
          await this.$store.dispatch('labels/create', {
            title: labelTitle.toLowerCase(),
            color: '#1f93ff',
            description: '',
          });
        } catch (error) {
          // 标签可能已存在
        }
      }

      if (!this.contactId) return;

      // 添加到联系人（合并已有标签和新标签）
      const allLabelsToAdd = [...existLabelsToAdd, ...newLabelsToAdd];
      const finalLabels = [...new Set([...this.chatLabels, ...allLabelsToAdd])];
      await this.$store.dispatch('contactLabels/update', {
        contactId: this.contactId,
        labels: finalLabels,
        conversationId: this.conversationId,
      });

      this.track(PILOT_EVENTS.LABEL_SUGGESTION_APPLIED);
      // 清空选择状态，但保留建议结果（标签状态会自动变成"已添加"）
      this.selectedLabels = [];
      this.selectedNewLabels = [];
      // 清除缓存，因为已添加的标签不需要再次建议
      this.clearCache();
    },

    dismiss() {
      LocalStorage.setFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.conversationId
      );
      this.isDismissed = true;
      this.suggestedLabels = [];
      this.hasAnalyzed = false;
      this.track(PILOT_EVENTS.LABEL_SUGGESTION_DISMISSED);
    },

    checkDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.conversationId
      );
    },

    track(event) {
      useTrack(event, {
        conversationId: this.conversationId,
        account: this.currentAccountId,
        suggestions: this.suggestedLabels,
      });
    },
  },
};
</script>

<template>
  <div
    v-if="shouldShow"
    class="label-suggestion"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- 加载动画 -->
    <div v-if="isLoading" class="suggestion-card">
      <div class="card-header">
        <div class="loading">
          <div class="spinner" />
          <span class="loading-text">
            {{ $t('LABEL_MGMT.SUGGESTIONS.LOADING')
            }}<span class="cursor">▋</span>
          </span>
        </div>
        <Icon
          v-tooltip.top="$t('LABEL_MGMT.SUGGESTIONS.POWERED_BY')"
          icon="i-lucide-sparkles"
          class="ai-icon"
        />
      </div>
      <div class="skeleton">
        <div class="skeleton-tag s1" />
        <div class="skeleton-tag s2" />
        <div class="skeleton-tag s3" />
      </div>
    </div>

    <!-- 建议结果（包含无结果提示） -->
    <div v-else class="suggestion-card">
      <div class="card-header">
        <div class="header-title">
          <Icon icon="i-lucide-lightbulb" class="title-icon" />
          {{ $t('LABEL_MGMT.SUGGESTIONS.SUGGESTED_LABELS') }}
        </div>
        <Icon
          v-tooltip.top="$t('LABEL_MGMT.SUGGESTIONS.POWERED_BY')"
          icon="i-lucide-sparkles"
          class="ai-icon"
        />
      </div>

      <!-- 无结果提示 -->
      <div v-if="showNoResult" class="no-result-hint">
        <Icon icon="i-lucide-info" class="hint-icon" />
        <span>{{ $t('LABEL_MGMT.SUGGESTIONS.NO_SUGGESTIONS') }}</span>
      </div>

      <!-- 精准匹配标签 -->
      <div v-if="matchedLabels.length > 0" class="labels-group">
        <div class="group-title">
          <Icon icon="i-lucide-tag" class="group-icon" />
          {{ $t('LABEL_MGMT.SUGGESTIONS.EXACT_MATCHES') }}
        </div>
        <div class="labels">
          <template v-for="label in matchedLabels" :key="label.title">
            <span
              v-if="isAlreadyAdded(label.title)"
              v-tooltip.top="$t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.ALREADY_ADDED')"
              class="label-tag added"
            >
              <Icon icon="i-lucide-check" class="check-icon" />
              {{ label.title }}
            </span>
            <button
              v-else
              v-tooltip.top="
                isSelected(label.title)
                  ? $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DESELECT')
                  : $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.SELECT')
              "
              type="button"
              class="label-tag"
              :class="{ selected: isSelected(label.title) }"
              @click="toggleLabel(label)"
            >
              {{ label.title }}
            </button>
          </template>
        </div>
      </div>

      <!-- AI 建议的新标签 -->
      <div
        v-if="unmatchedLabels.length > 0"
        class="labels-group"
        :class="{ 'with-divider': matchedLabels.length > 0 }"
      >
        <div class="group-title ai">
          <Icon icon="i-lucide-sparkles" class="group-icon" />
          {{ $t('LABEL_MGMT.SUGGESTIONS.AI_SUGGESTED') }}
        </div>
        <div class="labels">
          <button
            v-for="labelTitle in unmatchedLabels"
            :key="labelTitle"
            v-tooltip.top="
              isNewSelected(labelTitle)
                ? $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DESELECT')
                : $t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.SELECT')
            "
            type="button"
            class="label-tag new-label"
            :class="{ selected: isNewSelected(labelTitle) }"
            @click="toggleNewLabel(labelTitle)"
          >
            {{ labelTitle }}
          </button>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="card-footer">
        <button
          v-tooltip.top="$t('LABEL_MGMT.SUGGESTIONS.TOOLTIP.DISMISS')"
          type="button"
          class="dismiss-btn"
          @click="dismiss"
        >
          {{ $t('LABEL_MGMT.SUGGESTIONS.DISMISS') }}
        </button>
        <button
          v-if="addableLabels.length > 0 || unmatchedLabels.length > 0"
          type="button"
          class="add-btn"
          @click="addLabels"
        >
          {{ addButtonText || $t('LABEL_MGMT.SUGGESTIONS.ADD_ALL_LABELS') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.label-suggestion {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0 0.5rem;
}

.suggestion-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.5rem 0.625rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.dark .suggestion-card {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #475569;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.375rem;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #64748b;
}

.dark .header-title {
  color: #94a3b8;
}

.title-icon {
  width: 14px;
  height: 14px;
  color: #f59e0b;
}

.no-result-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.75rem;
  color: #64748b;
}

.dark .no-result-hint {
  color: #94a3b8;
}

.hint-icon {
  width: 14px;
  height: 14px;
  color: #94a3b8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.ai-icon {
  width: 14px;
  height: 14px;
  color: #8b5cf6;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.dismiss-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
}

.dark .dismiss-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* 标签分组样式 */
.labels-group {
  margin-bottom: 0.5rem;

  &.with-divider {
    padding-top: 0.5rem;
    margin-top: 0.375rem;
    border-top: 1px dashed #e2e8f0;
  }
}

.dark .labels-group.with-divider {
  border-top-color: #475569;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.625rem;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.ai {
    color: #8b5cf6;
  }
}

.dark .group-title {
  color: #94a3b8;

  &.ai {
    color: #a78bfa;
  }
}

.group-icon {
  width: 12px;
  height: 12px;
  color: #22c55e;

  .ai & {
    color: #8b5cf6;
  }
}

.labels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.label-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: 22px;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(.added) {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #3b82f6;
  }

  &.selected {
    border-color: #3b82f6;
    background: #3b82f6;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }

  &.added {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #94a3b8;
    cursor: default;
  }

  &.new-label {
    border-style: dashed;
    background: #fefce8;
    border-color: #fde047;
    color: #854d0e;

    &:hover {
      background: #fef9c3;
      border-color: #facc15;
    }

    &.selected {
      background: #facc15;
      border-color: #eab308;
      color: #422006;
      box-shadow: 0 2px 4px rgba(234, 179, 8, 0.3);
    }
  }
}

.dark .label-tag {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;

  &:hover:not(.added) {
    border-color: #60a5fa;
    background: #1e3a5f;
    color: #60a5fa;
  }

  &.selected {
    border-color: #3b82f6;
    background: #3b82f6;
    color: #ffffff;
  }

  &.added {
    background: #1e293b;
    border-color: #334155;
    color: #64748b;
  }

  &.new-label {
    background: #422006;
    border-color: #854d0e;
    color: #fef08a;

    &:hover {
      background: #713f12;
      border-color: #a16207;
    }

    &.selected {
      background: #a16207;
      border-color: #ca8a04;
      color: #fefce8;
    }
  }
}

.check-icon {
  width: 10px;
  height: 10px;
  color: #22c55e;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.375rem;
  margin-top: 0.375rem;
  border-top: 1px solid #e2e8f0;
}

.dark .card-footer {
  border-top-color: #475569;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  border: none;
  background: #3b82f6;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #2563eb;
  }
}

.dark .add-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

/* 加载动画样式 */
.loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #06b6d4);
  animation: spin 1.5s linear infinite;
  box-shadow: 0 0 8px rgba(139, 92, 246, 0.5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 0.75rem;
  color: #64748b;
}

.dark .loading-text {
  color: #94a3b8;
}

.cursor {
  animation: blink 1s step-end infinite;
  color: #8b5cf6;
  font-weight: bold;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.skeleton {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.skeleton-tag {
  height: 22px;
  border-radius: 9999px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;

  &.s1 {
    width: 60px;
  }
  &.s2 {
    width: 80px;
    animation-delay: 0.15s;
  }
  &.s3 {
    width: 50px;
    animation-delay: 0.3s;
  }
}

.dark .skeleton-tag {
  background: linear-gradient(90deg, #334155 25%, #475569 50%, #334155 75%);
  background-size: 200% 100%;
}

@keyframes shimmer {
  50% {
    background-position: -200% 0;
    opacity: 1;
  }
  0%,
  100% {
    background-position: 200% 0;
    opacity: 0.6;
  }
}

/* 错误信息样式 */
.error-message {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #64748b;
}

.dark .error-message {
  color: #94a3b8;
}

.info-icon {
  width: 14px;
  height: 14px;
  color: #94a3b8;
}
</style>
